import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLayoutEffect, useRef } from 'react';
import ImageFrame from '../components/ImageFrame.jsx';

gsap.registerPlugin(ScrollTrigger);

export default function VisualStream({ images, onFocusImage }) {
  const root = useRef(null);
  const opening = images.slice(0, 12);
  const horizontal = images.slice(12, 22);
  const closing = images.slice(22);

  useLayoutEffect(() => {
    if (!root.current || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const context = gsap.context(() => {
      gsap.utils.toArray('.story-card').forEach((card, index) => {
        gsap.fromTo(
          card,
          {
            autoAlpha: 0,
            y: index % 2 ? 120 : 70,
            clipPath: index % 2 ? 'inset(0 0 100% 0)' : 'inset(18% 0 18% 0)'
          },
          {
            autoAlpha: 1,
            y: 0,
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 1.2,
            ease: 'power4.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 82%',
              end: 'bottom 45%',
              scrub: 0.65
            }
          }
        );
      });

      gsap.utils.toArray('.image-frame img').forEach((image) => {
        const depth = Number(image.closest('.image-frame')?.dataset.depth || 1);
        gsap.to(image, {
          yPercent: -11 * depth,
          scale: 1.08,
          ease: 'none',
          scrollTrigger: {
            trigger: image.closest('.image-frame'),
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        });
      });

      const track = root.current.querySelector('.horizontal__track');
      if (track) {
        gsap.to(track, {
          x: () => -(track.scrollWidth - window.innerWidth + 96),
          ease: 'none',
          scrollTrigger: {
            trigger: '.horizontal',
            start: 'top top',
            end: () => `+=${Math.max(track.scrollWidth, window.innerWidth)}`,
            pin: true,
            scrub: 1,
            invalidateOnRefresh: true
          }
        });
      }
    }, root);

    return () => context.revert();
  }, [images.length]);

  return (
    <section className="visual-stream" id="work" ref={root}>
      <div className="stream-prologue">
        <p className="mono-label">Selected visual current</p>
        <h2>Ein endloser Schnitt durch Gesichter, Kanten, Licht und Stille.</h2>
      </div>

      <div className="story-lane story-lane--opening">
        {opening.map((image, index) => (
          <ImageFrame
            image={image}
            className={`story-card story-card--${image.variant} ${image.focus ? 'is-focus' : ''}`}
            key={image.id}
            priority={index < 2}
            onFocus={onFocusImage}
          />
        ))}
      </div>

      {horizontal.length > 0 && (
        <div className="horizontal">
          <div className="horizontal__intro">
            <p className="mono-label">Horizontal rupture</p>
            <h3>The page breaks direction.</h3>
          </div>
          <div className="horizontal__track">
            {horizontal.map((image) => (
              <ImageFrame image={image} className="horizontal-card" key={image.id} onFocus={onFocusImage} />
            ))}
          </div>
        </div>
      )}

      <div className="story-lane story-lane--closing">
        {closing.map((image) => (
          <ImageFrame
            image={image}
            className={`story-card story-card--${image.variant} ${image.focus ? 'is-focus' : ''}`}
            key={image.id}
            onFocus={onFocusImage}
          />
        ))}
      </div>
    </section>
  );
}
